import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Send, Loader2 } from "lucide-react";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate>
          <Outlet />
        </AuthGate>
        <Toaster
          theme="dark"
          richColors
          position="top-right"
          toastOptions={{
            classNames: {
              toast:
                "border border-border bg-surface text-zinc-100 ring-1 ring-white/5 font-mono text-xs",
            },
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === "checking") return <SplashLoader />;
  if (state.status === "anonymous") return <LoginScreen message={state.error} />;
  return <>{children}</>;
}

function SplashLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-ember" />
    </div>
  );
}

function LoginScreen({ message }: { message?: string }) {
  const { login } = useAuth();
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>(message);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setErr(undefined);
    try {
      await login(token);
    } catch (ex) {
      setErr((ex as { error?: string }).error ?? "Invalid token.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <form
        onSubmit={onSubmit}
        className="fade-up w-full max-w-sm rounded-xl border border-border bg-surface/40 p-8 ring-1 ring-white/5"
      >
        <div className="mb-8 flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-sm bg-ember ring-4 ring-ember/10">
            <Send className="h-4 w-4 text-background" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-semibold tracking-tight text-zinc-100">
              Obsidian Mail
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              ops console
            </div>
          </div>
        </div>

        <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Admin token
        </label>
        <input
          type="password"
          autoFocus
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.currentTarget.value)}
          placeholder="Paste your ADMIN_TOKEN…"
          className="mb-3 h-10 w-full rounded-md border border-border bg-background px-3 font-mono text-sm text-zinc-200 placeholder:text-zinc-500 focus:border-ember/60 focus:outline-none focus:ring-2 focus:ring-ember/20"
        />
        {err && (
          <div className="mb-3 rounded-md border border-ember/40 bg-ember/10 px-3 py-2 font-mono text-[11px] text-ember">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy || !token.trim()}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-ember text-sm font-semibold text-background ring-1 ring-ember/40 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </button>

        <p className="mt-6 font-mono text-[10px] leading-relaxed text-zinc-500">
          Token is verified against the Worker's
          <span className="text-zinc-400"> ADMIN_TOKEN</span> secret and held in
          sessionStorage only for the current tab.
        </p>
      </form>
    </div>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {error.message || "Something went wrong on our end."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
